import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, X, CheckCircle } from "lucide-react";
import { Button, PageContent } from "@printtat/ui";
import { paymentApi } from "@/services/paymentApi";
import { useCartStore } from "@/stores/cartStore";

export const PaymentCallbackPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get("orderId");

  const [status, setStatus] = useState<
    "polling" | "success" | "failed" | "timeout" | "error"
  >("polling");

  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const didStartRef = useRef(false);

  const stopPolling = useCallback(() => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
    if (pollTimeoutRef.current) {
      clearTimeout(pollTimeoutRef.current);
      pollTimeoutRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!orderId || didStartRef.current) return;
    didStartRef.current = true;

    const poll = async () => {
      try {
        const result = await paymentApi.getStatus(orderId);
        if (result.paymentStatus === "paid") {
          stopPolling();
          setStatus("success");
          useCartStore.setState({ items: [] });
          setTimeout(() => navigate(`/orders/${orderId}`, { replace: true }), 1500);
        } else if (result.paymentStatus === "failed") {
          stopPolling();
          setStatus("failed");
        }
      } catch {
        // Continue polling
      }
    };

    // Trigger reconciliation once on mount — Paymob redirected here with
    // success=true in the URL, so the payment is almost certainly paid.
    // If the webhook didn't land, this recovers it without waiting on the
    // 3am cron. Fire-and-forget; polling below will see the new status.
    paymentApi.reconcile(orderId).catch(() => {
      // Best-effort — polling handles the slow path.
    });

    pollTimerRef.current = setInterval(poll, 3000);
    poll();

    pollTimeoutRef.current = setTimeout(() => {
      stopPolling();
      setStatus("timeout");
    }, 10 * 60 * 1000);

    return () => stopPolling();
  }, [orderId, navigate, stopPolling]);

  if (!orderId) {
    return (
      <PageContent>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center space-y-4">
            <p className="text-sm text-gray-500">Missing order information.</p>
            <Button onClick={() => navigate("/orders", { replace: true })}>
              View Orders
            </Button>
          </div>
        </div>
      </PageContent>
    );
  }

  return (
    <PageContent>
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center space-y-4 max-w-sm">
          {status === "polling" && (
            <>
              <Loader2 className="w-8 h-8 animate-spin text-gray-400 mx-auto" />
              <p className="text-sm text-gray-600">
                Confirming your payment...
              </p>
            </>
          )}

          {status === "success" && (
            <>
              <CheckCircle className="w-12 h-12 text-green-500 mx-auto" />
              <p className="text-sm font-medium text-gray-900">
                Payment confirmed! Redirecting to your order...
              </p>
            </>
          )}

          {status === "failed" && (
            <>
              <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mx-auto">
                <X className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900 mb-1">
                  Payment Failed
                </p>
                <p className="text-sm text-gray-500">
                  Your payment was not successful.
                </p>
              </div>
              <Button
                onClick={() => navigate(`/orders/${orderId}`, { replace: true })}
              >
                View Order
              </Button>
            </>
          )}

          {status === "timeout" && (
            <>
              <Loader2 className="w-8 h-8 text-amber-500 mx-auto" />
              <div>
                <p className="text-sm font-medium text-gray-900 mb-1">
                  Payment is Taking Longer Than Expected
                </p>
                <p className="text-sm text-gray-500">
                  Your payment may still be processing.
                </p>
              </div>
              <Button
                onClick={() => navigate(`/orders/${orderId}`, { replace: true })}
              >
                View Order
              </Button>
            </>
          )}

          {status === "error" && (
            <>
              <X className="w-8 h-8 text-red-500 mx-auto" />
              <p className="text-sm text-gray-500">
                Something went wrong.
              </p>
              <Button onClick={() => navigate("/orders", { replace: true })}>
                View Orders
              </Button>
            </>
          )}
        </div>
      </div>
    </PageContent>
  );
};
